/**
 * Adding files to a note: checks each file's type and size, uploads images and small videos with
 * the note's add-files request, and large videos in parts, then adds those to the note. Shared by
 * `AddMemoFileForm` and the desktop project record's note pane. `notify: false` leaves the
 * messages to the caller, which reads the result.
 */
import { Memo } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { queryKeys } from '../../../../../hooks/react-query/constants';
import {
  useAddFilesToMemoMutation,
  useUpdateMemoMutation,
} from '../../../../../hooks/react-query/useMemoQuery';
import {
  isVideoFile,
  shouldUseMultipartUpload,
  useLargeFileUploadMutation,
} from '../../../../../hooks/react-query/useS3';
import {
  generateUniqueFileName,
  isValidFileType,
  MAX_FILE_SIZE_BYTES,
  mergeFileIdCsv,
} from '../../../../../tools/fileUploadUtils';
import { MemoClientService } from '../../../../../tools/helpers';

export type AddMemoFilesResult =
  | { status: 'none-valid' }
  | { status: 'done'; added: number; skipped: number }
  | { status: 'error' };

export const useAddMemoFiles = ({
  memoId,
  notify = true,
}: {
  memoId: number;
  notify?: boolean;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: addFiles } = useAddFilesToMemoMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.memoList.root],
      });
    },
  });

  const { mutateAsync: uploadLargeFile, isPending: isLargeUploading } =
    useLargeFileUploadMutation();

  const { mutateAsync: updateMemo } = useUpdateMemoMutation();

  const [isUploadingLargeVideos, setIsUploadingLargeVideos] = useState(false);

  const uploadFiles = async (
    files: File[],
    onUploaded?: () => void,
  ): Promise<AddMemoFilesResult> => {
    setIsSubmitting(true);
    try {
      // Validate each file's type and size
      const validationResults = await Promise.all(
        files.map(async (file) => ({
          file,
          isValid: (await isValidFileType(file)) && file.size <= MAX_FILE_SIZE_BYTES,
        })),
      );

      const validFiles = validationResults.filter((r) => r.isValid).map((r) => r.file);
      const invalidFiles = validationResults.filter((r) => !r.isValid).map((r) => r.file);

      if (validFiles.length === 0) {
        if (notify)
          toast({
            title: 'Upload Failed',
            description:
              'Invalid file type or size detected. Allowed types: JPEG, PNG, WEBP, HEIC (\u2264150 MB each) and MP4, WEBM, MOV (\u2264150 MB each).',
            variant: 'destructive',
          });
        setIsSubmitting(false);
        return { status: 'none-valid' };
      }

      if (invalidFiles.length > 0 && notify) {
        toast({
          title: 'Some files skipped',
          description: `${invalidFiles.length} file(s) were skipped due to unsupported file type.`,
          variant: 'destructive',
        });
      }

      // Separate valid files into regular and large videos
      const regularFiles: File[] = [];
      const largeVideoFiles: File[] = [];

      validFiles.forEach((file) => {
        if (isVideoFile(file) && shouldUseMultipartUpload(file)) {
          largeVideoFiles.push(file);
        } else {
          regularFiles.push(file);
        }
      });

      // Regular files: the mutation stages images and assigns unique names.
      if (regularFiles.length > 0) {
        await addFiles({ memoId, files: regularFiles });
      }

      // Fetch current memo to get latest fileId csv after regular upload
      const latestMemo = await MemoClientService.Get(Memo.create({ id: memoId }));
      let existingFileIdCsv = latestMemo?.fileId || '';

      // Handle large videos
      if (largeVideoFiles.length > 0) {
        setIsUploadingLargeVideos(true);

        const uploadedIds: number[] = [];

        for (const file of largeVideoFiles) {
          const uniqueName = generateUniqueFileName(file.name);

          const uploaded = await uploadLargeFile({
            file,
            bucket: 'testbuckethelios',
            key: uniqueName,
          });

          if (uploaded?.id) {
            uploadedIds.push(uploaded.id);
          }
        }

        setIsUploadingLargeVideos(false);

        if (uploadedIds.length > 0) {
          const mergedCsv = mergeFileIdCsv(existingFileIdCsv, uploadedIds);

          await updateMemo(Memo.create({ id: memoId, fileId: mergedCsv, fieldMask: ['FileId'] }));

          existingFileIdCsv = mergedCsv;
        }
      }

      if (notify) {
        toast({
          title: 'Files added successfully',
          description: `${validFiles.length} file(s) have been added to the note`,
        });
      }

      onUploaded?.();
      return { status: 'done', added: validFiles.length, skipped: invalidFiles.length };
    } catch (error) {
      console.error('Error uploading files:', error);
      if (notify)
        toast({
          title: 'Error uploading files',
          description: 'There was an error uploading one or more files. Please try again.',
          variant: 'destructive',
        });
      return { status: 'error' };
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUploading = isSubmitting || isUploadingLargeVideos || isLargeUploading;

  return {
    uploadFiles,
    isSubmitting,
    isUploadingLargeVideos,
    isLargeUploading,
    isUploading,
  };
};
